import { EditorState, RichUtils } from 'draft-js';
import BlockButton from './BlockButton';
import StyleButton from './StyleButton';
import { BLOCK_TYPES, INLINE_STYLES, MAX_LIST_DEPTH } from './config/types';

import decorateComponentWithProps from 'decorate-component-with-props';

const richButtonsPlugin = () => {
  const store = {
    getEditorState: undefined,
    setEditorState: undefined,
    currentState: undefined,

    onChange: function onChange(newState) {
      if (newState!==this.currentState) {
        this.currentState = newState;
        this.notifyBound();
      }
      return newState;
    },

    // buttons must be subscribed explicitly to ensure rerender
    boundComponents: [],
    bindToState: function bindToState(component, remove) {
      if (remove) {
        this.boundComponents = this.boundComponents.filter((registered) =>
          registered!==component
        );
      } else {
        this.boundComponents.push(component);
      }
    },
    notifyBound: function notifyBound() {
      this.boundComponents.forEach((component) => component.forceUpdate());
    },

    toggleInlineStyle: function toggleInlineStyle(inlineStyle) {
      const state = this.getEditorState();
      const newState = RichUtils.toggleInlineStyle(
        state,
        inlineStyle
      );
      this.setEditorState(
        newState
      );
    },

    toggleBlockType: function toggleBlockType(blockType) {
      const state = this.getEditorState();
      const newState = RichUtils.toggleBlockType(
        state,
        blockType
      );
      this.setEditorState(
        EditorState.forceSelection(
          newState, newState.getCurrentContent().getSelectionAfter()
        )
      );
    }
  };

  const configured = {
    initialize: ({ getEditorState, setEditorState }) => {
      store.currentState = getEditorState();
      store.getEditorState = () => store.currentState;
      store.setEditorState = (newState) => {
        store.onChange(newState);
        setEditorState(newState);
      };
    },

    handleKeyCommand: (editorState, command) => {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      return newState
    },

    onTab: (event, editor) => {
      return RichUtils.onTab(event, editor.getEditorState(), MAX_LIST_DEPTH);
    },

    onChange: (newState) => {
      // Preserve inline style override when focus changes to the editor
      // This handles the case where a style button is keyboard-activated
      // and then the user tabs into the editor
      const prevState = store.currentState;
      const prevOverride = prevState?.getInlineStyleOverride();
      const newOverride = newState.getInlineStyleOverride();

      if (prevOverride && !newOverride && newState.getSelection().isCollapsed()) {
        newState = EditorState.setInlineStyleOverride(newState, prevOverride);
      }

      return store.onChange(newState);
    }
  };

  INLINE_STYLES.forEach((inlineStyle) => {
    configured[`${inlineStyle.label}Button`] = decorateComponentWithProps(
      StyleButton, {
        store,
        bindToState: store.bindToState.bind(store),
        label: inlineStyle.label,
        inlineStyle: inlineStyle.style
      }
    );
  });

  BLOCK_TYPES.forEach((blockType) => {
    configured[`${blockType.label}Button`] = decorateComponentWithProps(
      BlockButton, {
        store,
        bindToState: store.bindToState.bind(store),
        label: blockType.label,
        blockType: blockType.style
      }
    );
  });

  configured.createBlockButton = ({type, label}) => decorateComponentWithProps(
    BlockButton, {
      store,
      bindToState: store.bindToState.bind(store),
      label: label,
      blockType: type
    }
  );

  configured.createStyleButton = ({style, label}) => decorateComponentWithProps(
    StyleButton, {
      store,
      bindToState: store.bindToState.bind(store),
      label: label,
      inlineStyle: style
    }
  );

  return configured;
};

export { richButtonsPlugin };
